import type {AUTH_TYPE, SavedLoginAccount} from '@Libraries/login-signup-library';
import {useAppSelector} from '@/hooks';

export const useLoginPageAuthType = () => {
  return useAppSelector((state) => {
    return state.loginPage.authType;
  });
};

export const useLoginPageAuthSubType = () => {
  return useAppSelector((state) => {
    return state.loginPage.authSubType;
  });
};

export const useLoginPageAuthMode = () => {
  return useAppSelector((state) => {
    return state.loginPage.authMode;
  });
};

export const useLoginPageErrorMessage = () => {
  return useAppSelector((state) => {
    return state.loginPage.errorMessage;
  });
};

export const useLoginPageShowError = () => {
  return useAppSelector((state) => {
    return state.loginPage.showError;
  });
};

export const useIsLoginToProceedMessage = () => {
  return useAppSelector((state) => {
    return state.loginPage.isLoginToProceedMessage;
  });
};

export const useLoginPageAuthButtonsDisabled = () => {
  return useAppSelector((state) => {
    return state.loginPage.areAuthButtonsDisabled;
  });
};

export const useLoginPageAnimateNotificationBanner = () => {
  return useAppSelector((state) => {
    return state.loginPage.animateNotificationBanner;
  });
};

export const useLoginPageLoginActionType = () => {
  return useAppSelector((state) => {
    return state.loginPage.loginActionType;
  });
};

export const useLoginPageConfirmLoginType = () => {
  return useAppSelector((state) => {
    return state.loginPage.confirmLoginType;
  });
};

/**
 * A page-level error that should mark a field as the cause. The "log in to proceed" prompt is a page
 * message rather than a failure, so it deliberately leaves the fields alone.
 */
export const useIsLoginPageFieldErrorActive = (): boolean => {
  const showError = useLoginPageShowError();
  const areFieldsMarkedInvalid = useAppSelector((state) => {
    return state.loginPage.areFieldsMarkedInvalid;
  });
  const isLoginToProceedMessage = useIsLoginToProceedMessage();

  return showError && areFieldsMarkedInvalid && !isLoginToProceedMessage;
};

export const useIsSignupBlocked = (): boolean => {
  return useAppSelector((state) => {
    return state.loginPage.isSignupBlocked;
  });
};

export const useLoginPageTwoFactorVerificationOrigin = (): AUTH_TYPE => {
  return useAppSelector((state) => {
    return state.loginPage.twoFactorVerificationOrigin;
  });
};

export const useLoginPageRedirectUrl = (): string => {
  return useAppSelector((state) => {
    return state.loginPage.redirectUrl ?? '';
  });
};

export const useLoginPageSignupEmail = (): string => {
  return useAppSelector((state) => {
    return state.loginPage.signupEmail;
  });
};

export const useLoginPageForgotPasswordEmail = (): string => {
  return useAppSelector((state) => {
    return state.loginPage.forgotPasswordEmail;
  });
};

export const useLoginPageSavedAccounts = (): SavedLoginAccount[] => {
  return useAppSelector((state) => {
    return state.loginPage.savedAccounts;
  });
};

export const useLoginPageSelectedSavedAccountHashedId = (): string | null => {
  return useAppSelector((state) => {
    return state.loginPage.selectedSavedAccountHashedId;
  });
};

export const useIsRemovingSavedAccounts = (): boolean => {
  return useAppSelector((state) => {
    return state.loginPage.isRemovingSavedAccounts;
  });
};
